import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/hooks/useLanguage";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface FAQ {
  id: string;
  category: string;
  question: string;
  answer: string;
  question_en: string | null;
  answer_en: string | null;
  question_fr: string | null;
  answer_fr: string | null;
  sort_order: number;
}

const FAQ = () => {
  const { language, t } = useLanguage();

  const { data: faqs, isLoading } = useQuery({
    queryKey: ["faqs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("faqs")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data as FAQ[];
    },
  });

  const getQuestion = (faq: FAQ) => {
    if (language === "en" && faq.question_en) return faq.question_en;
    if (language === "fr" && faq.question_fr) return faq.question_fr;
    return faq.question;
  };

  const getAnswer = (faq: FAQ) => {
    if (language === "en" && faq.answer_en) return faq.answer_en;
    if (language === "fr" && faq.answer_fr) return faq.answer_fr;
    return faq.answer;
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, Record<string, string>> = {
      global: { es: "Global", en: "Global", fr: "Global" },
      europa: { es: "Europa", en: "Europe", fr: "Europe" },
      norte_america: { es: "Norte América", en: "North America", fr: "Amérique du Nord" },
    };
    return labels[category]?.[language] || category;
  };

  const categories = ["global", "europa", "norte_america"];

  const faqsByCategory = (category: string) =>
    faqs?.filter((faq) => faq.category === category) || [];

  return (
    <>
      <SEO
        title={t("faq.title")}
        description={t("faq.subtitle")}
        keywords="safari FAQ, preguntas frecuentes safari, África safari preguntas"
      />
      <Navbar />
      <main className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-b from-primary/10 to-background">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{t("faq.title")}</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("faq.subtitle")}
            </p>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            {isLoading ? (
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : !faqs || faqs.length === 0 ? (
              <p className="text-center text-muted-foreground">{t("faq.noQuestions")}</p>
            ) : (
              <Tabs defaultValue="global" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-8">
                  {categories.map((cat) => (
                    <TabsTrigger key={cat} value={cat}>
                      {getCategoryLabel(cat)}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {categories.map((cat) => (
                  <TabsContent key={cat} value={cat}>
                    {faqsByCategory(cat).length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        {t("faq.noCategoryQuestions")}
                      </p>
                    ) : (
                      <Accordion type="single" collapsible className="w-full">
                        {faqsByCategory(cat).map((faq) => (
                          <AccordionItem key={faq.id} value={faq.id}>
                            <AccordionTrigger className="text-left">
                              {getQuestion(faq)}
                            </AccordionTrigger>
                            <AccordionContent className="text-muted-foreground whitespace-pre-wrap">
                              {getAnswer(faq)}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default FAQ;